import type {
    DeviceTypePayload,
    DeviceTypeResponse,
} from '@/types/apps/device-type.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Exportable } from '@/types/service/exportable.interface';
import type { Importable } from '@/types/service/importable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

export interface DeviceTypeServiceInterface {}

type DeviceTypeListResponse = ApiResponse<PaginatedResult<DeviceTypeResponse>>;

export class DeviceTypeService
    implements
        Showable,
        Updatable,
        Deletable,
        Creatable,
        Indexable,
        Importable,
        Exportable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/device-type',
        detail: (id: string) => `/api/device-type/${id}`,
        import: () => '/api/device-type/import',
        export: () => '/api/device-type/export',
    };

    public index(params?: QueryParams): Promise<DeviceTypeListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<DeviceTypeResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: DeviceTypePayload
    ): Promise<ApiResponse<DeviceTypeResponse>> {
        const body = this.apiClient.buildFormData(payload);

        return this.apiClient.postApi(this.endpoints.base(), body);
    }

    public update(
        id: string,
        payload: DeviceTypePayload
    ): Promise<ApiResponse<DeviceTypeResponse>> {
        const body = this.apiClient.buildFormData(payload);

        return this.apiClient.putApi(this.endpoints.detail(id), body);
    }
    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
